import awsRumActionNames from '../../consts/aws/awsRumActionNames.js';
import awsRumActionTypes from '../../consts/aws/awsRumActionTypes';
import awsRumCustomEvents from '../../consts/aws/awsRumCustomEvents';
import awsRumStatusTypes from '../../consts/aws/awsRumStatusTypes.js';
import {selectIsAuthorized} from '../../state/selectors/auth';
import {selectIsOnline} from '../../state/selectors/global';
import {selectActiveDusn, selectIsAutomaticFWInstallationActive} from '../../state/selectors/iotDevices';
import {selectGlobalModals} from '../../state/selectors/modal';
import {selectConsentsFlagsValues} from '../../state/selectors/session.js';
import {getState} from '../../state/store';
import awsRum from '../aws/rum/awsRum';
import log from '../logger/log';
import ecEventKeys from './ecEventKeys';
import rumCustomEventsDBService from './rumCustomEventsDBService';
import rumCustomEventsHelpers from './rumCustomEventsHelpers';

let isSending = false;

const SPECIAL_DURATION_TYPES = [
    awsRumCustomEvents.SETTINGS,
    awsRumCustomEvents.FIRMWARE_UPDATE,
    awsRumCustomEvents.SYNCHRONIZE,
];

const pushEvent = (type, {dusn, startTime, action, rumActionName, ...eventData}) => {
    const appData = rumCustomEventsHelpers.collectAppData();
    const pageData = rumCustomEventsHelpers.collectPageData();
    const deviceData = rumCustomEventsHelpers.collectDeviceData(dusn);
    const initialDeviceData = rumCustomEventsHelpers.collectInitialDeviceData();
    const formattedAction = rumCustomEventsHelpers.formatAction(action, rumActionName);
    const duration = SPECIAL_DURATION_TYPES.includes(type)
        ? rumCustomEventsHelpers.getDuration(startTime)
        : rumCustomEventsHelpers.getAdjustedDuration(startTime);
    const {timeSinceLastEvent, previousAction} = rumCustomEventsHelpers.getLastEventData();
    const modals = selectGlobalModals(getState());
    const modalType = eventData?.modalType || modals?.[modals.length - 1]?.modalType || null; // eventData?.modalType used when modal hides

    const event = {
        ...appData,
        ...pageData,
        ...deviceData,
        ...initialDeviceData,
        timeSinceLastEvent,
        previousAction,
        ...eventData,
        ...(!!modalType && {modalType}),
        ...(!!duration && {duration}),
        ...(!!formattedAction && {action: formattedAction}),
    };

    rumCustomEventsHelpers.updateLastEventData(formattedAction);

    log.info(`awsRUM: ${type} ${JSON.stringify(event)}`);

    recordCustomEvents(type, event);
};

export const recordCustomEvents = async (type, eventData) => {
    const isPaused = rumCustomEventsHelpers.getIsPushEventsPaused();
    const isOnline = selectIsOnline(getState());
    const isAuthorized = selectIsAuthorized(getState());

    if (isPaused || !isOnline || !isAuthorized) {
        await rumCustomEventsDBService.addEventToIndexedDB(type, eventData);
        log.info('awsRUM: Event queued in IndexedDB.');
        return;
    }

    awsRum.recordCustomEvents(type, eventData);

    if (isSending) return;
    isSending = true;

    const ready = await rumCustomEventsDBService.listReadyForSend();

    for (const rec of ready) {
        awsRum.recordCustomEvents(rec.type, rec.eventData);
        await rumCustomEventsDBService.markSent(rec.id);
    }

    isSending = false;
};

const pushSettings = (options = {}) => pushEvent(awsRumCustomEvents.SETTINGS, options);

const pushFirmwareUpdate = (options) => {
    const {dusn, ...rest} = options || {};
    const isAutoFWInstallationActive = selectIsAutomaticFWInstallationActive(dusn)(getState());
    const rumParameters = rumCustomEventsHelpers.getFirmwareUpdateParams() || {};

    rumCustomEventsHelpers.clearFirmwareUpdateParams();

    pushEvent(awsRumCustomEvents.FIRMWARE_UPDATE, {dusn, isAutoFWInstallationActive, ...rumParameters, ...rest});
};

const pushFirmwareUpdateSuccess = (dusn, requestId) =>
    pushFirmwareUpdate({dusn, status: awsRumStatusTypes.SUCCESS, eventId: requestId});

const pushFirmwareUpdateFailed = (dusn, e) =>
    pushFirmwareUpdate({dusn, status: awsRumStatusTypes.FAILED, eventId: e?.requestId, errorType: e?.errorType});

const pushSynchronize = (options = {}) => pushEvent(awsRumCustomEvents.SYNCHRONIZE, options);

const pushPushNotification = (options = {}) =>
    pushEvent(awsRumCustomEvents.NOTIFICATIONS, {
        ...options,
        ...ecEventKeys.getNotificationKeys(options.notificationSubType),
    });

const pushUserConsents = (options = {}) => {
    const consents = selectConsentsFlagsValues(getState());

    pushEvent(awsRumCustomEvents.USER_CONSENTS, {consents, ...options});
};

const recordAction = ({dusn = selectActiveDusn(getState()), ...options} = {}) => {
    const {currentFlow, ...currentFlowParameters} = rumCustomEventsHelpers.getStartParametersForCurrentFlow();

    pushEvent(currentFlow, {
        dusn,
        ...options,
        ...currentFlowParameters,
        ...ecEventKeys.getHostActionKeys(options.action, options.recommendationType),
    });
};

const recordUwmAction = ({dusn = selectActiveDusn(getState()), moduleName, ...options} = {}) => {
    pushEvent(awsRumCustomEvents.UWM_EVENT, {
        dusn,
        ...(moduleName && {moduleName}),
        ...options,
        ...ecEventKeys.getUwmKeys({moduleName, ...options}),
    });
};

const recordActionClick = ({rumActionName = '', ...options} = {}) =>
    recordAction({...options, action: awsRumActionTypes.CLICK, rumActionName});

const recordActionSelect = ({id, isSelected, ...rest} = {}) =>
    recordActionClick({
        rumActionName: `${isSelected ? awsRumActionNames.DESELECT : awsRumActionNames.SELECT}_${id}`,
        ...rest,
    });

const recordActionSwipe = ({rumActionName = ''} = {}) => recordAction({action: awsRumActionTypes.SWIPE, rumActionName});

const recordModalAction = ({isVisible, modalType = ''}) => {
    if (modalType) {
        rumCustomEvents.recordAction({
            action: isVisible ? awsRumActionTypes.VISIBLE : awsRumActionTypes.HIDDEN,
            rumActionName: awsRumActionNames.MODAL,
            modalType,
        });
    }
};

const recordUwmModuleVisibility = ({isVisible, moduleName}) =>
    rumCustomEvents.recordAction({
        action: isVisible ? awsRumActionTypes.VISIBLE : awsRumActionTypes.HIDDEN,
        rumActionName: awsRumActionNames.MODAL,
        ...(moduleName && {moduleName}),
    });

const recordWiFiConfiguration = (options) => {
    recordAction({action: awsRumActionTypes.WIFI_CONFIGURATION, ...options});
};

const recordWiFiConfigurationStarted = () => {
    recordWiFiConfiguration({status: awsRumStatusTypes.STARTED});
};
const recordWiFiConfigurationSuccess = (dusn) => {
    recordWiFiConfiguration({status: awsRumStatusTypes.SUCCESS, dusn});
};
const recordWiFiConfigurationFailed = (errorType) => {
    recordWiFiConfiguration({status: awsRumStatusTypes.FAILED, errorType});
};
const recordWiFiConfigurationError = (errorType) =>
    recordWiFiConfiguration({status: awsRumStatusTypes.ERROR, errorType});

const rumCustomEvents = {
    pushSettings,
    pushFirmwareUpdateSuccess,
    pushFirmwareUpdateFailed,
    pushSynchronize,
    pushPushNotification,
    pushUserConsents,
    recordAction,
    recordUwmAction,
    recordActionClick,
    recordActionSelect,
    recordActionSwipe,
    recordModalAction,
    recordUwmModuleVisibility,
    recordWiFiConfiguration,
    recordWiFiConfigurationStarted,
    recordWiFiConfigurationSuccess,
    recordWiFiConfigurationFailed,
    recordWiFiConfigurationError,
};

export default rumCustomEvents;
